import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import useUpdateShopPreferences from "@babackapp/private-api-client/client/use-update-shop-preferences";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Form, FormField } from "@babackapp/ui/components/form";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { OverrideRulesManager } from "../override-rules";

const LogisticianOptionsFormValues = z.object({
  autoTreateOnLogisticianValidate: z.boolean().default(false),
});

export type LogisticianOptionsFormValuesType = z.infer<
  typeof LogisticianOptionsFormValues
>;

export function LogisticianOptionsForm() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateShopPreference } = useUpdateShopPreferences(
    shop.id,
  );

  const form = useForm<LogisticianOptionsFormValuesType>({
    defaultValues: {
      autoTreateOnLogisticianValidate:
        shop.preferences.autoTreateOnLogisticianValidate || false,
    },
  });

  const values = useWatch({ control: form.control });

  const onSubmit = form.handleSubmit(
    async ({ autoTreateOnLogisticianValidate }) => {
      updateShopPreference({ autoTreateOnLogisticianValidate }).then(() =>
        form.reset({ autoTreateOnLogisticianValidate }),
      );
    },
  );

  useEffect(() => {
    if (form.formState.isDirty) onSubmit();
  }, [values, form.formState.isDirty]);

  const planActivated = true || !!shop.plan.workflowLogistician;
  const isAdmin = useViewerHasAtLeastAdminRights();

  const disabledBecausePlanNotActivated = planActivated
    ? false
    : t("settings.shop.errors.planNotActivated");

  const disabledBecauseNotAdmin = isAdmin
    ? false
    : t("settings.shop.errors.notAdmin");

  const disabledBecauseWorkflowNotActivated = shop.workflowLogistician.activated
    ? false
    : t("settings.shop.errors.logisticianWorfklowNotActivated");

  const switchIsDisabled =
    disabledBecauseNotAdmin ||
    disabledBecausePlanNotActivated ||
    disabledBecauseWorkflowNotActivated;

  return (
    <div>
      <Form {...form}>
        <form onSubmit={onSubmit}>
          <FormField
            disabled={!isAdmin}
            control={form.control}
            name="autoTreateOnLogisticianValidate"
            render={({ field }) => (
              <FormSwitch
                label={t(
                  "settings.customerPortal.basics.fields.autoTreateOnLogisticianValidate.title",
                )}
                description={t(
                  "settings.customerPortal.basics.fields.autoTreateOnLogisticianValidate.description",
                )}
                {...field}
                disabled={switchIsDisabled}
              />
            )}
          />
        </form>
      </Form>
      <OverrideRulesManager actionType={"OVERRIDE_AUTO_TREATMENT"} />
    </div>
  );
}
